import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BoosterMilestoneFormValues, BoosterProgramDetail, BoosterProgramFormValues } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import {
  BOOSTER_MILESTONE_STATUS_CODE,
  BOOSTER_PROGRAM_STATUS_TO_CODE,
} from 'constants/booster-program'
import {
  mapFormToBoosterProgramCreatePayload,
  mapMilestoneFormToCreatePayload,
} from 'modules/booster-program/api-adapters'
import { logError, logInfo } from 'utils/logger'

import { boosterProgramKeys } from './query-keys'

const invalidateLists = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: boosterProgramKeys.lists() })

const invalidateDetail = (queryClient: ReturnType<typeof useQueryClient>, id: string) =>
  queryClient.invalidateQueries({ queryKey: boosterProgramKeys.detail(id) })

export const useCreateBoosterProgram = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      values,
      status,
    }: {
      values: BoosterProgramFormValues
      status: 'SCHEDULED' | 'DRAFT'
    }) => {
      const payload = {
        ...mapFormToBoosterProgramCreatePayload(values),
        status: BOOSTER_PROGRAM_STATUS_TO_CODE[status],
      }
      logInfo('Create booster program payload', { payload })
      const res = await AppAdmin.createBoosterProgram(payload)
      if (!res.data?.success || !res.data.data?._id) {
        throw new Error(res.data?.message || 'Failed to create booster program')
      }
      return res.data.data
    },
    onSuccess: () => {
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Create booster program failed', err),
  })
}

export const useUpdateBoosterProgram = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      values,
      status,
    }: {
      id: string
      values: BoosterProgramFormValues
      status?: 'SCHEDULED' | 'DRAFT'
    }) => {
      const payload = {
        ...mapFormToBoosterProgramCreatePayload(values),
        ...(status ? { status: BOOSTER_PROGRAM_STATUS_TO_CODE[status] } : null),
      }
      logInfo('Update booster program payload', { id, payload })
      const res = await AppAdmin.updateBoosterProgram(id, payload)
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to update booster program')
      }
      return res.data.data
    },
    onSuccess: (_, vars) => {
      invalidateDetail(queryClient, vars.id)
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Update booster program failed', err),
  })
}

/**
 * Upload a whitelist file for a program (targetUser = "others"). The BE replaces
 * the entire existing whitelist with the addresses parsed from the new file.
 */
export const useUploadBoosterProgramWhitelist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ programId, file }: { programId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await AppAdmin.uploadBoosterProgramWhitelist(programId, formData)
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to upload whitelist')
      }
      return res.data.data
    },
    onSuccess: (_, vars) => {
      invalidateDetail(queryClient, vars.programId)
    },
    onError: (err) => logError('Upload booster whitelist failed', err),
  })
}

export const useAddBoosterMilestones = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      programId,
      milestones,
    }: {
      programId: string
      milestones: BoosterMilestoneFormValues[]
    }) => {
      const payload = milestones.filter(Boolean).map(mapMilestoneFormToCreatePayload)
      logInfo('Add booster milestones payload', { programId, payload })
      const res = await AppAdmin.addBoosterMilestones(programId, payload)
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to add milestones')
      }
      return res.data.data
    },
    onSuccess: (_, vars) => {
      invalidateDetail(queryClient, vars.programId)
    },
    onError: (err) => logError('Add booster milestones failed', err),
  })
}

export const useUpdateBoosterMilestone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      programId,
      milestoneId,
      values,
    }: {
      programId: string
      milestoneId: string
      values: BoosterMilestoneFormValues
    }) => {
      const payload = mapMilestoneFormToCreatePayload(values)
      logInfo('Update booster milestone payload', { programId, milestoneId, payload })
      const res = await AppAdmin.updateBoosterMilestone(programId, milestoneId, payload)
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to update milestone')
      }
      return res.data.data
    },
    onSuccess: (_, vars) => {
      invalidateDetail(queryClient, vars.programId)
    },
    onError: (err) => logError('Update booster milestone failed', err),
  })
}

export const useDeleteBoosterMilestone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ programId, milestoneId }: { programId: string; milestoneId: string }) => {
      const res = await AppAdmin.updateBoosterMilestone(programId, milestoneId, {
        status: BOOSTER_MILESTONE_STATUS_CODE.DELETED,
      })
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to delete milestone')
      }
      return milestoneId
    },
    onSuccess: (_, vars) => {
      invalidateDetail(queryClient, vars.programId)
    },
    onError: (err) => logError('Delete booster milestone failed', err),
  })
}

export const useDeleteBoosterProgram = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await AppAdmin.updateBoosterProgram(id, {
        status: BOOSTER_PROGRAM_STATUS_TO_CODE.DELETE,
      })
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to delete booster program')
      }
      return id
    },
    onSuccess: () => {
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Delete booster program failed', err),
  })
}

export const useChangeBoosterProgramStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: BoosterProgramDetail['status'] }) => {
      const res = await AppAdmin.updateBoosterProgram(id, {
        status: BOOSTER_PROGRAM_STATUS_TO_CODE[status],
      })
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Failed to update status')
      }
      return { id, status }
    },
    onSuccess: (_, vars) => {
      invalidateDetail(queryClient, vars.id)
      invalidateLists(queryClient)
    },
  })
}
